/**
 * Parser ligero de X-Ray Radiation Dose SR (RDSR).
 * Recorre Content Sequence (0040,A730) buscando CTDIvol / DLP / DAP.
 */
import * as dicomParser from 'dicom-parser'
import { isRdsrSopClass } from './isRdsrSeries'

function seqItems(ds, tag) {
  if (!ds || !ds.elements) return []
  const el = ds.elements[tag]
  if (!el || !Array.isArray(el.items)) return []
  return el.items.map((it) => it.dataSet || it).filter(Boolean)
}

function str(ds, tag) {
  if (!ds || typeof ds.string !== 'function') return null
  try {
    const v = ds.string(tag)
    return v != null && String(v).trim() ? String(v).trim() : null
  } catch (e) {
    return null
  }
}

function conceptCode(ds) {
  // Concept Name Code Sequence (0040,A043)
  const items = seqItems(ds, 'x0040a043')
  if (!items.length) return { value: null, meaning: null }
  const c = items[0]
  return {
    value: str(c, 'x00080100'),
    meaning: str(c, 'x00080104'),
  }
}

function measuredValue(ds) {
  // Measured Value Sequence (0040,A300)
  const items = seqItems(ds, 'x0040a300')
  if (!items.length) return { value: null, unit: null }
  const m = items[0]
  const value = parseFloat(str(m, 'x0040a30a') || '')
  const unitItems = seqItems(m, 'x004008ea') // Measurement Units Code Sequence
  const unit =
    unitItems.length > 0
      ? str(unitItems[0], 'x00080100') || str(unitItems[0], 'x00080104')
      : null
  return {
    value: Number.isFinite(value) ? value : null,
    unit,
  }
}

const CTDIVOL_CODES = new Set(['113083', '113091'])
const DLP_CODES = new Set(['113838', '113835'])
const DAP_CODES = new Set(['113722', '113507', '122130'])

function matchMetric(code, meaning) {
  const m = String(meaning || '').toLowerCase()
  if (CTDIVOL_CODES.has(code) || m.includes('ctdi')) return 'ctdivol'
  if (DLP_CODES.has(code) || (m.includes('dlp') || m.includes('dose length'))) return 'dlp'
  if (
    DAP_CODES.has(code) ||
    m.includes('dose area') ||
    m.includes('dap') ||
    m.includes('kerma area')
  ) {
    return 'dap'
  }
  return null
}

function walkContent(ds, acc) {
  if (!ds) return
  const vt = str(ds, 'x0040a040') // Value Type
  const { value: code, meaning } = conceptCode(ds)

  if (vt === 'NUM') {
    const metric = matchMetric(code, meaning)
    const { value, unit } = measuredValue(ds)
    if (metric && value != null) {
      // Preferir el máximo / último significativo (acumulados suelen ser CONTAINER totales)
      if (acc[metric] == null || value > acc[metric]) {
        acc[metric] = value
        acc[`${metric}Unit`] = unit
      }
      acc.entries.push({
        metric,
        value,
        unit,
        code,
        meaning,
      })
    }
  }

  const children = seqItems(ds, 'x0040a730')
  for (const child of children) {
    walkContent(child, acc)
  }
}

/**
 * @param {object} dataSet dicom-parser dataset
 * @returns {{ ctdivol: number|null, dlp: number|null, dap: number|null, entries: object[], source: string }|null}
 */
export function parseRdsrDataSet(dataSet) {
  if (!dataSet) return null
  const sop = str(dataSet, 'x00080016')
  if (sop && !isRdsrSopClass(sop)) {
    // Permitir Enhanced SR si contiene códigos de dosis
  }

  const acc = {
    ctdivol: null,
    dlp: null,
    dap: null,
    ctdivolUnit: null,
    dlpUnit: null,
    dapUnit: null,
    entries: [],
    source: 'rdsr',
  }

  const roots = seqItems(dataSet, 'x0040a730')
  if (!roots.length) {
    walkContent(dataSet, acc)
  } else {
    for (const root of roots) walkContent(root, acc)
  }

  if (acc.ctdivol == null && acc.dlp == null && acc.dap == null) {
    return { ...acc, empty: true }
  }
  return acc
}

/**
 * @param {ArrayBuffer|Uint8Array} byteArray
 */
export function parseRdsrBytes(byteArray) {
  const buf =
    byteArray instanceof ArrayBuffer
      ? new Uint8Array(byteArray)
      : byteArray
  const dataSet = dicomParser.parseDicom(buf)
  return parseRdsrDataSet(dataSet)
}

/**
 * Desde un file item del visor (image.data o bytes crudos).
 * @param {object} fileItem
 */
export function parseRdsrFromFileItem(fileItem) {
  if (!fileItem) return null
  if (fileItem.image && fileItem.image.data) {
    return parseRdsrDataSet(fileItem.image.data)
  }
  return null
}
